import { createEntityQuery, Order } from '@datorama/akita';
import { map } from 'rxjs';
import store from './store';
import { type Organisation } from 'types/models';

// Case-insensitive, locale-aware sort by name (fallback to code). Applied at every tree level
// so the whole hierarchy renders in a consistent, predictable order.
const compareByDisplayName = (a: Organisation, b: Organisation): number =>
   (a?.name ?? a?.code ?? '').localeCompare(b?.name ?? b?.code ?? '', undefined, { sensitivity: 'base' });

/**
 * Builds the organisation forest from the flat list the user may access.
 *
 * Users with rights on individual entities only receive those entities, so the hierarchy can have gaps:
 * an accessible entity whose parent is not accessible. Every entity without a known parent becomes a root,
 * so such entities are never dropped, even when there are several of them.
 */
export function nestOrganisations(items: Organisation[], id: Organisation['id'] | null = null): Organisation[] {
   if (id == null) {
      const knownIds = new Set(items.map((item) => item.id));

      return items
         .filter((item) => !(item?.parents ?? []).some((parent) => knownIds.has(parent?.id)))
         .map((item) => ({ ...item, children: nestOrganisations(items, item.id) }))
         .sort(compareByDisplayName);
   }

   return items
      .filter((item) => (item?.parents ?? []).find((parent) => parent?.id === id))
      .map((item) => ({ ...item, children: nestOrganisations(items, item.id) }))
      .sort(compareByDisplayName);
}

export const query = createEntityQuery(store, {
   sortBy: 'id',
   sortByOrder: Order.ASC,
});

export const allEntities$ = query.selectAll().pipe(
   map((organisations) =>
      organisations.map((organisation) => ({
         ...organisation,
         label: organisation.code ? `(${organisation.code}) ${organisation.name}` : organisation.name,
         value: organisation.id,
      }))
   )
);

export const entities$ = allEntities$.pipe(map((organisations) => organisations.filter((org) => !org.deletedAt)));

export const activeEntity$ = query.selectActive();

export const allEntitiesTree$ = allEntities$.pipe(map((organisations) => nestOrganisations(organisations)));

export const entityTree$ = entities$.pipe(map((organisations) => nestOrganisations(organisations ?? [])));

export const statistics$ = query.select('statistics');
export const loading$ = query.selectLoading();

export const entitiesBeingDeleted$ = query.selectAll({
   filterBy: (entity) => !!entity?.deleteOperationStatus,
});
